import Button from '@/components/Button/newButtons';
import { AI, AISequenceBackground } from '@/resources/assets/images';
import { Textarea } from '@/components/atoms/Textarea';
import { Input } from '@/components/atoms/Input';
import { Dialog } from '@/components/atoms/Dialog';
import { createAiCampaign } from '@/services/sequencesService';
import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useSequencesContext } from '@/modules/sequencesRedesign/SequencesContext';
import { sendAnalytics } from '@/services/analyticsService';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '@/rootReducer';
import { AiSequenceSchemaProps } from '@/schemas/aiSequence/schema';
import { ErrorMessage } from '@/components/atoms/ErrorMessage';
import { cn } from '@/lib/classNames';
import { useIsScreenWidthSmallerThan } from '@/hooks/useIsScreenWidthSmallerThan';
import { useAnimatedEllipses } from '@/hooks/useAnimatedEllipses';
import { useFormValidation } from '../hooks/useFormValidation';
import { useBorderRotation } from '../hooks/useBorderRotation';

interface AiSequenceDialogFormProps {
	trigger: ReactNode;
	isOpen?: boolean;
	setIsOpen?: (isOpen: boolean) => void;
}

const AiSequenceDialogForm = ({
	trigger,
	isOpen,
	setIsOpen
}: AiSequenceDialogFormProps) => {
	const [loading, setLoading] = useState(false);
	const [isModalOpen, setIsModalOpen] = useState(false);

	const { setSequencePage } = useSequencesContext();
	const boxRef = useBorderRotation(loading);
	const ellipses = useAnimatedEllipses(loading);
	const screenSize = useIsScreenWidthSmallerThan(380);

	const {
		selectedSequence,
		setSelectedTemplate,
		setSelectedStep,
		setSelectedSequence,
		setAiSequenceModalFields,
		aiSequenceModalFields
	} = useSequencesContext();

	const {
		register,
		setValue,
		handleSubmit,
		formState: { errors }
	} = useFormValidation();

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const buttonLabel = useMemo(() => {
		if (loading) return `Generating${ellipses}`;

		return aiSequenceModalFields
			? 'Regenerate AI Sequence'
			: 'Create AI Sequence';
	}, [loading, ellipses, aiSequenceModalFields]);

	const handleModalOpenChange = (open: boolean) => {
		setIsModalOpen(open);

		if (setIsOpen) {
			setIsOpen(open);
		}

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: `User ${open ? 'opened' : 'closed'}  the ${aiSequenceModalFields ? 'regenerate' : 'create'} AI sequence`
		});
	};

	const onSubmit = async (formData: AiSequenceSchemaProps) => {
		setLoading(true);

		sendAnalytics({
			type: 'track',
			userId: user.email,
			eventName: aiSequenceModalFields
				? 'User clicked to regenerate AI sequence'
				: 'User clicked to create AI sequence'
		});

		try {
			const aiResponse = await createAiCampaign({
				offering: formData.offering,
				targetAudience: formData.targetAudience,
				valueProposition: formData.valueProposition,
				signature: formData.signature
			});

			if (aiResponse.status !== 200) {
				toast.error(
					aiResponse.data?.message ?? 'An unexpected error occurred.'
				);

				sendAnalytics({
					type: 'track',
					userId: user.email,
					eventName: aiSequenceModalFields
						? 'Failed to regenerate AI sequence'
						: 'Failed to generate AI sequence'
				});

				return;
			}

			const campaignData = aiResponse.data?.data;

			if (!campaignData) {
				throw new Error('Failed to create AI campaign');
			}

			const {
				name,
				description,
				isAiGenerated,
				steps = []
			} = campaignData;

			const processedSteps = steps.map((step, index) => ({
				...step,
				id: `id-${index}`,
				body: step.body.replace(/\n/g, '<br>')
			}));

			setSelectedTemplate({
				name,
				description,
				isAiGenerated,
				steps: processedSteps
			});

			setSelectedSequence({
				name,
				status: 'DRAFT',
				steps: processedSteps,
				id: selectedSequence?.id ?? undefined,
				justCreated: true
			});

			setSelectedStep({
				...steps[0],
				id: 'id-0',
				body: steps[0].body.replace(/\n/g, '<br>')
			});

			setAiSequenceModalFields({
				offering: formData.offering,
				targetAudience: formData.targetAudience,
				valueProposition: formData.valueProposition,
				signature: formData.signature
			});

			setLoading(false);
			setIsModalOpen(false);
			setSequencePage('STEPS-CREATION');

			toast.success('AI campaign generated successfully!');

			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: aiSequenceModalFields
					? 'AI sequence regenerated'
					: 'AI sequence generated',
				properties: {
					offering: formData.offering,
					targetAudience: formData.targetAudience,
					valueProposition: formData.valueProposition,
					signature: formData.signature
				}
			});
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: 'An unexpected error occurred.'
			);

			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: aiSequenceModalFields
					? 'Failed to regenerate AI sequence'
					: 'Failed to generate AI sequence'
			});
		}
	};

	useEffect(() => {
		setValue('offering', aiSequenceModalFields?.offering ?? '');
		setValue('targetAudience', aiSequenceModalFields?.targetAudience ?? '');
		setValue(
			'valueProposition',
			aiSequenceModalFields?.valueProposition ?? ''
		);
		setValue('signature', aiSequenceModalFields?.signature ?? '');
	}, [aiSequenceModalFields, setValue]);

	return (
		<Dialog.Root
			open={isOpen ?? isModalOpen}
			onOpenChange={handleModalOpenChange}
			modal={!screenSize.isSmaller}
		>
			<Dialog.Trigger asChild>{trigger}</Dialog.Trigger>

			<Dialog.Content
				data-is-loading={loading}
				ref={boxRef}
				style={
					{
						'--angle': '0deg',
						'--border-color':
							'linear-gradient(var(--angle), #BC82F3, #F5B9EA, #8D99FF, #AA6EEE, #FF6778, #FFBA71, #C686FF)',
						'--bg-color': 'linear-gradient(#212121, #212121)'
					} as CSSProperties
				}
				className={cn(
					"!absolute z-150 grid w-full max-w-[calc(100vw-1.5rem)] grid-cols-[30ch_1fr] gap-x-8 overflow-hidden rounded-lg border-2 border-dm-grey bg-dm-black p-0 text-dm-white shadow-modal data-[is-loading='true']:border-[#0000] data-[is-loading='true']:[background:padding-box_var(--bg-color),border-box_var(--border-color)] lg:!fixed lg:max-w-4xl",
					screenSize.isSmaller && 'mt-24'
				)}
			>
				<div className="relative isolate hidden bg-dm-black1 px-5 py-6 text-base lg:block">
					<AISequenceBackground className="absolute inset-0 -z-10 h-full w-full" />

					<div className="flex items-center gap-0.5 font-semibold">
						<AI className="text-dm-gold" width={25} height={25} />

						<span className="text-sm text-orange-brighter">
							Re2 AI
						</span>
					</div>

					<h2 className="my-8 text-lg font-semibold leading-tight">
						AI-powered - <br /> sequence creation
					</h2>

					<p className="mb-3 font-light">
						Accelerate your outreach by letting our AI handle the
						heavy lifting.
					</p>

					<p className="font-light">
						Provide a few essential details, and we’ll generate a
						tailored campaign to help you reach the right
						audience—fast.
					</p>
				</div>

				<form
					onSubmit={handleSubmit(onSubmit)}
					className="col-span-2 flex flex-col gap-4 px-4 py-6 lg:col-span-1 lg:pl-0 lg:pr-8"
				>
					<Dialog.Header className="mb-4">
						<Dialog.Title className="font-semibold">
							Review your settings
						</Dialog.Title>

						<Dialog.Description className="sr-only">
							Create an AI sequence by providing a few essential
							details
						</Dialog.Description>
					</Dialog.Header>

					<div>
						<label
							htmlFor="offering"
							className="text-sm font-semibold"
						>
							What Are You Offering?{' '}
						</label>

						<p className="mb-2 mt-1 text-xs font-light">
							Describe your product or service so we can build a
							relevant sequence{' '}
						</p>

						<Input
							size="md"
							rounded="md"
							color="contrast"
							padding="lg"
							id="offering"
							placeholder="e.g. restaurant inventory software"
							{...register('offering')}
						/>

						{errors.offering && (
							<ErrorMessage message={errors.offering.message} />
						)}
					</div>

					<div>
						<label
							htmlFor="targetAudience"
							className="text-sm font-semibold"
						>
							Who Is Your Audience?
						</label>

						<p className="mb-2 mt-1 text-xs font-light">
							Describe your product or service to help us generate
							the most relevant business list for you.
						</p>

						<Input
							size="md"
							rounded="md"
							color="contrast"
							padding="lg"
							id="targetAudience"
							placeholder="e.g. restaurants in Austin"
							{...register('targetAudience')}
						/>

						{errors.targetAudience && (
							<ErrorMessage
								message={errors.targetAudience.message}
							/>
						)}
					</div>

					<div>
						<label
							htmlFor="valueProposition"
							className="text-sm font-semibold"
						>
							What’s your value proposition{' '}
						</label>

						<p className="mb-2 mt-1 text-xs font-light">
							Highlight the key benefits that make your offering
							stand out.
						</p>

						<Textarea
							size="md"
							id="valueProposition"
							placeholder="e.g. Save 10% on inventory costs, used by 300+ restaurants"
							{...register('valueProposition')}
						/>

						{errors.valueProposition && (
							<ErrorMessage
								message={errors.valueProposition.message}
							/>
						)}
					</div>

					<div>
						<label
							htmlFor="signature"
							className="text-sm font-semibold"
						>
							Signature / Contact info
						</label>

						<p className="mb-2 mt-1 text-xs font-light">
							Include any personal details or your standard email
							signature.
						</p>

						<Textarea
							id="signature"
							size="md"
							{...register('signature')}
						/>

						{errors.signature && (
							<ErrorMessage message={errors.signature.message} />
						)}
					</div>

					<Dialog.Footer className="flex-row !justify-end gap-6 border-t-[1px] border-dm-grey pt-8">
						<Dialog.Close asChild>
							<Button variant="text">Cancel</Button>
						</Dialog.Close>

						{loading ? (
							<span className="flex w-[7.5rem] cursor-not-allowed select-none items-center rounded-lg border-2 border-dm-blue px-4 text-sm font-semibold">
								{buttonLabel}
							</span>
						) : (
							<Button
								smart
								type="submit"
								className="justify-start"
							>
								{buttonLabel}
							</Button>
						)}
					</Dialog.Footer>
				</form>
			</Dialog.Content>
		</Dialog.Root>
	);
};

export default AiSequenceDialogForm;
